import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, ShieldCheck, UserX, UserCheck, Crown, Users, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const ROLES = [
  { value: "all", label: "All roles" },
  { value: "candidate", label: "Seekers" },
  { value: "recruiter", label: "Recruiters" },
  { value: "admin", label: "Admins" },
];

export default function AdminUsersPage() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = { limit: 300 };
      if (q) params.q = q;
      if (role !== "all") params.role = role;
      const r = await api.get("/admin/directory/users", { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch {
      toast.error("Failed to load users");
    }
  }, [q, role]);

  useEffect(() => { load(); }, [load]);

  const setRoleFor = async (u, newRole) => {
    if (newRole === u.role) return;
    setBusy(u.user_id);
    try {
      await api.patch(`/admin/users/${u.user_id}`, { role: newRole });
      toast.success(`${u.name || u.email} is now ${newRole}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(null); }
  };

  const toggleDisable = async (u) => {
    setBusy(u.user_id);
    try {
      await api.patch(`/admin/users/${u.user_id}`, { is_disabled: !u.is_disabled });
      toast.success(u.is_disabled ? "Account re-enabled" : "Account disabled");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(null); }
  };

  const togglePlus = async (u) => {
    setBusy(u.user_id);
    try {
      await api.patch(`/admin/users/${u.user_id}`, { is_subscribed: !u.is_subscribed });
      toast.success(u.is_subscribed ? "Plus revoked" : "Granted 30 days Plus");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(null); }
  };

  if (!user || user.role !== "admin") {
    return (
      <div className="container py-20 max-w-xl text-center" data-testid="admin-users-locked">
        <h1 className="display-2 mb-4">Admin only</h1>
        <p className="text-muted-foreground">Sign in as <code>admin@karm.app</code>.</p>
      </div>
    );
  }

  return (
    <div className="container py-10">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="admin-users-back">
        <ArrowLeft className="h-4 w-4" /> Back to Admin Console
      </Link>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <p className="eyebrow mb-1 inline-flex items-center gap-2"><Users className="h-3 w-3" /> User management</p>
          <h1 className="display-2">{total.toLocaleString()} accounts</h1>
        </div>
      </div>

      <div className="vital-card-static p-3 md:p-4 mb-6 grid md:grid-cols-[1fr_220px] gap-2" data-testid="admin-users-filters">
        <div className="flex items-center gap-2 px-3 border border-border rounded-lg bg-background">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…" className="border-0 focus-visible:ring-0 shadow-none" data-testid="admin-users-search" />
        </div>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger data-testid="admin-users-role"><SelectValue /></SelectTrigger>
          <SelectContent>
            {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border" data-testid="admin-users-table">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr className="text-left text-xs text-muted-foreground uppercase tracking-wider">
              <th className="py-2 px-3">User</th>
              <th className="py-2 px-3">Role</th>
              <th className="py-2 px-3">Provider</th>
              <th className="py-2 px-3">Plus</th>
              <th className="py-2 px-3">Status</th>
              <th className="py-2 px-3">Joined</th>
              <th className="py-2 px-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.map((u) => (
              <tr key={u.user_id} data-testid={`admin-user-${u.user_id}`}>
                <td className="py-2 px-3">
                  <div className="font-semibold">{u.name}</div>
                  <div className="text-xs text-muted-foreground">{u.email}</div>
                </td>
                <td className="py-2 px-3">
                  <Select value={u.role} onValueChange={(v) => setRoleFor(u, v)} disabled={u.user_id === user.user_id || busy === u.user_id}>
                    <SelectTrigger className="h-8 w-32" data-testid={`admin-user-role-${u.user_id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="candidate">Seeker</SelectItem>
                      <SelectItem value="recruiter">Recruiter</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
                <td className="py-2 px-3"><span className="pill">{u.auth_provider}</span></td>
                <td className="py-2 px-3">
                  {u.is_subscribed
                    ? <span className="inline-flex items-center gap-1 text-vital-blue text-xs"><Crown className="h-3 w-3" />Plus</span>
                    : <span className="text-xs text-muted-foreground">free</span>}
                </td>
                <td className="py-2 px-3">
                  {u.is_disabled
                    ? <span className="text-xs text-amber-600 inline-flex items-center gap-1"><UserX className="h-3 w-3" />disabled</span>
                    : <span className="text-xs text-emerald-600 inline-flex items-center gap-1"><UserCheck className="h-3 w-3" />active</span>}
                </td>
                <td className="py-2 px-3 text-xs text-muted-foreground">{new Date(u.created_at).toLocaleDateString()}</td>
                <td className="py-2 px-3 text-right">
                  <div className="inline-flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => togglePlus(u)} disabled={busy === u.user_id || u.user_id === user.user_id} data-testid={`admin-user-plus-${u.user_id}`}>
                      {u.is_subscribed ? "Revoke Plus" : "Grant Plus"}
                    </Button>
                    <Button size="sm" variant={u.is_disabled ? "default" : "outline"} onClick={() => toggleDisable(u)} disabled={busy === u.user_id || u.user_id === user.user_id} data-testid={`admin-user-disable-${u.user_id}`}>
                      {u.is_disabled ? "Enable" : "Disable"}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={7} className="py-10 text-center text-muted-foreground" data-testid="admin-users-empty">No users match.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-muted-foreground mt-4 inline-flex items-center gap-1.5">
        <ShieldCheck className="h-3 w-3" /> Admin actions are audited server-side. You cannot modify your own account here.
      </p>
    </div>
  );
}
